/**
 * BL-743: Contentstack hands us colour choices as Tailwind class strings, and
 * a class only paints if Tailwind generated a rule for it at build time.
 *
 * The colour dropdowns store their values as arbitrary classes — Navy is
 * `bg-[#0C2848]`, not `bg-navy`. Tailwind mints arbitrary values by scanning
 * source, so a hex that exists only in the CMS at runtime has no rule and
 * never will: the class lands on the element and paints nothing. Rather than
 * ask the CMS to re-enter 47 option values, we translate the hex back to the
 * palette slug here.
 *
 * Translating rather than generating the hex classes is deliberate. Emitting
 * `bg-[#0C2848]` from `@source inline` would work today and then quietly go
 * wrong the day Brand revises a colour: the token would move and the arbitrary
 * class would keep painting the old hex. Going through the slug means the
 * value always comes from `var(--color-navy)`, and a hex we do not recognise
 * warns in dev instead of failing silently.
 */

// Keyed by uppercase hex without the `#`. Mirrors the palette in
// app/globals.css — if a colour is added there, add it here too, or the CMS
// value for it will warn in dev and render as no colour.
const HEX_TO_TOKEN = {
  '102409': 'green-darkest',
  '2A572E': 'green-forest',
  '0C2848': 'navy',
  '0C1E35': 'navy-muted',
  '2D3E50': 'slate-blue',
  '43547E': 'blue-gray',
  '4A7EC9': 'blue-medium',
  '0065F4': 'blue-vibrant',
  '74A7D5': 'sky-blue',
  '8FA4D1': 'periwinkle-blue',
  B8D4F5: 'light-blue',
  E2F1FB: 'pale-blue',
  DAE8F3: 'powder-blue',
  CEEAFF: 'ice-blue',
  '372C6E': 'purple-deep',
  '280707': 'maroon-dark',
  '490A0A': 'blood-red',
  '5E0B0B': 'maroon',
  '891D1A': 'brick-red',
  '351916': 'dark-brown',
  '81513D': 'brown',
  A22E0E: 'rust',
  DD441B: 'orange-red',
  F6C041: 'gold-yellow',
  CAB19B: 'tan',
  '787A61': 'olive-gray',
  E6E6E6: 'light-gray',
  F1E6D2: 'cream',
  F1EBDB: 'ivory',
  F6F2F0: 'off-white',
  FCE7E4: 'blush-pink',
  '00513E': 'green-deep',
};

const ARBITRARY_HEX = /^(bg|text|border)-\[#([0-9a-fA-F]{6})\]$/;

export function cmsColorClass(value, fallback = '') {
  const className = typeof value === 'string' ? value.trim() : '';

  if (!className) return fallback;

  const hex = className.match(ARBITRARY_HEX);
  if (hex) {
    const [, prefix, digits] = hex;
    const token = HEX_TO_TOKEN[digits.toUpperCase()];

    if (token) return `${prefix}-${token}`;

    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[cmsColorClass] "${className}" is a hex the palette does not know, so no CSS rule ` +
          'exists for it and it will render as no colour at all. Add it to HEX_TO_TOKEN in ' +
          'utils/cmsColorClass.js alongside the token in app/globals.css.',
      );
    }

    return className;
  }

  if (process.env.NODE_ENV === 'development' && /^[a-z-]+-\[/.test(className)) {
    console.warn(
      `[cmsColorClass] "${className}" is an arbitrary Tailwind value coming from the CMS, ` +
        'so no CSS rule exists for it and it will render as no colour at all. ' +
        'Use the palette slug instead.',
    );
  }

  return className;
}

export default cmsColorClass;
